import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';

interface WizardFooterProps {
  currentStep: number;
  totalSteps: number;
  canGoBack: boolean;
  canGoNext: boolean;
  onBack: () => void;
  onNext: () => void;
  isSaving?: boolean;
  lastSaved?: Date | null;
}

export function WizardFooter({
  currentStep,
  totalSteps,
  canGoBack,
  canGoNext,
  onBack,
  onNext,
  isSaving,
  lastSaved
}: WizardFooterProps) {
  useLanguage();
  const maxSteps = Math.max(totalSteps || 0, 1);
  const clampedCurrent = Math.min(Math.max(currentStep || 0, 0), maxSteps);
  const progressPercent = Math.min(Math.max((clampedCurrent / maxSteps) * 100, 0), 100);
  const isFinalStep = totalSteps > 0 && currentStep === totalSteps;

  const formatLastSaved = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    
    if (diffSecs < 10) return 'agora';
    if (diffSecs < 60) return `${diffSecs}s atrás`;
    
    const diffMins = Math.floor(diffSecs / 60);
    if (diffMins < 60) return `${diffMins}min atrás`;
    
    return date.toLocaleTimeString(getInterfaceLanguage(), { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="sticky bottom-0 z-20 bg-card/100 backdrop-blur-sm border-t shadow-lg">
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Progress */}
          <div className="flex-1 max-w-xs">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-foreground whitespace-nowrap">
                {Math.round(progressPercent)}{translateInterfaceText("% concluído")}</span>
              <Progress value={progressPercent} className="h-2 flex-1" />
            </div>
            {lastSaved && !isSaving && (
              <p className="text-xs text-muted-foreground mt-1">
                {translateInterfaceText("Último salvamento:")}{" "}{formatLastSaved(lastSaved)}
              </p>
            )}
            {isSaving && (
              <p className="text-xs text-muted-foreground mt-1">
                {translateInterfaceText("Salvando...")}</p>
            )}
          </div>

          {/* Navigation */}
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={onBack}
              disabled={!canGoBack}
              className="gap-2"
            >
              <ChevronLeft className="h-4 w-4" />
              {translateInterfaceText("Voltar")}</Button>
            
            <Button
              onClick={onNext}
              disabled={!canGoNext}
              className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {isFinalStep ? translateInterfaceText("Finalizar") : translateInterfaceText("Continuar")}
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
